import { useMutation, useQuery } from "convex/react";
import {
  Loader2,
  Lock,
  LockOpen,
  RefreshCw,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { ReferentielPyramide } from "@repo/results";
import { api } from "../../../../convex/_generated/api";
import type {
  StatDimension as StatDimensionQsort,
  StatFourchette,
} from "../../../../convex/referentiel/agregation";
import { niveauxForcesQsort } from "../../../../convex/referentiel/agregation";
import type { Id } from "../../../../convex/_generated/dataModel";
import { ReferentielConfigSection } from "./ReferentielConfigSection";
import { ReferentielFourchetteCard } from "./ReferentielFourchetteCard";
import { ReferentielItemsCard } from "./ReferentielItemsCard";
import { dateCourte } from "@repo/utils";

type Props = {
  campagneId: Id<"campagnes">;
  /** Référentiel affiché — celui du test sélectionné dans la campagne. */
  cible: "pami" | "pc-fit";
  /** Portail admin : consultation seule (ni configuration ni figement). */
  lectureSeule?: boolean;
};

/**
 * Écran « état du référentiel » (§8.2). Affiche l'identité de la version
 * courante — c'est elle qui doit accompagner TOUT score de matching, export
 * compris — et remonte le consensus, que la moyenne écrase par construction.
 */
export function ReferentielTab({
  campagneId,
  cible,
  lectureSeule = false,
}: Props) {
  const etat = useQuery(api.referentiel.campagne.obtenirEtatReferentiel, {
    campagneId,
    cible,
  });
  const figer = useMutation(api.referentiel.campagne.figerReferentiel);
  const rouvrir = useMutation(api.referentiel.campagne.rouvrirReferentiel);
  const recalculer = useMutation(
    api.referentiel.campagne.recalculerReferentiel,
  );

  if (etat === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (etat === null) {
    if (lectureSeule) {
      return (
        <Card>
          <CardHeader>
            <CardTitle>Référentiel</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Aucun référentiel configuré pour cette campagne. La configuration
              se fait depuis le portail client.
            </p>
          </CardContent>
        </Card>
      );
    }
    return (
      <ReferentielConfigSection campagneId={campagneId} cible={cible} />
    );
  }

  const { versionCourante, referents, config } = etat;
  const nbContributions = referents.filter(
    (r) => r.statut === "termine",
  ).length;
  // PAMI : référent unique. Consensus, désaccord et seuil de 3 contributions
  // n'ont pas d'objet — les afficher quand même produirait un avertissement
  // permanent, qui n'avertit plus de rien.
  const referentUnique = config.cible === "pami";
  const libelleDimension = (code: string) =>
    config.dimensions.find((d) => d.code === code)?.label ?? code;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-3">
            Référentiel de la campagne
            {versionCourante && (
              <>
                <Badge variant="info">
                  v{versionCourante.version} · {versionCourante.nbReferents}{" "}
                  référent{versionCourante.nbReferents > 1 ? "s" : ""} ·{" "}
                  {dateCourte(versionCourante.computedAt)}
                </Badge>
                <Badge variant={versionCourante.fige ? "purple" : "outline"}>
                  {versionCourante.fige ? "Figé" : "Ouvert"}
                </Badge>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!versionCourante ? (
            // Des contributions validées SANS projection : la campagne n'a plus
            // aucun déclencheur de calcul (rien à soumettre), il faut un point
            // d'entrée explicite — sinon le référentiel reste vide alors que les
            // données brutes sont là.
            nbContributions > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {nbContributions} contribution
                  {nbContributions > 1 ? "s" : ""} validée
                  {nbContributions > 1 ? "s" : ""} mais aucune version calculée.
                  Relancez le calcul pour reconstruire le référentiel à partir
                  des réponses déjà collectées.
                </p>
                {!lectureSeule && (
                  <Button onClick={() => void recalculer({ campagneId, cible })}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Recalculer le référentiel
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Aucun référent n'a encore contribué : le référentiel est
                configuré mais vide.
              </p>
            )
          ) : (
            <>
              {!referentUnique &&
                versionCourante.dimensionsEnDesaccord.length > 0 && (
                  <div className="text-sm">
                    <p className="font-medium">Dimensions en désaccord</p>
                    <p className="text-muted-foreground">
                      Des référents proposent des plages disjointes — la moyenne
                      affichée ne représente aucun d'eux :{" "}
                      {versionCourante.dimensionsEnDesaccord
                        .map(libelleDimension)
                        .join(", ")}
                      .
                    </p>
                  </div>
                )}

              {versionCourante.dimensionsNeutralisees.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium">
                    Dimensions hors calcul (
                    {versionCourante.dimensionsNeutralisees.length})
                  </p>
                  <p className="text-muted-foreground">
                    Non concernées ou couvrant toute l'échelle : elles ne pèsent
                    ni en faveur ni en défaveur des candidats.
                  </p>
                </div>
              )}

              <div
                className={`flex flex-wrap items-center gap-3 ${lectureSeule ? "hidden" : ""}`}
              >
                {versionCourante.fige ? (
                  <Button
                    variant="outline"
                    onClick={() => void rouvrir({ campagneId, cible })}
                  >
                    <LockOpen className="mr-2 h-4 w-4" />
                    Rouvrir le référentiel
                  </Button>
                ) : (
                  <Button onClick={() => void figer({ campagneId, cible })}>
                    <Lock className="mr-2 h-4 w-4" />
                    Figer le référentiel
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  Figez le référentiel avant de commencer à comparer des
                  candidats : tant qu'il est ouvert, une contribution tardive
                  fait bouger tous les scores en même temps. C'est aussi le
                  figement qui CONSERVE la version — une version ouverte est
                  réécrite à chaque nouvelle contribution.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Le référentiel lui-même, avant la liste brute des items : une pyramide
          se lit d'un coup d'œil là où un tableau de moyennes demande un effort.
          Q-sort uniquement — une cible en fourchette (PAMI) n'a pas de niveau. */}
      {config.echelle.kind === "qsort" && versionCourante && (
        <ReferentielPyramide
          dimensions={config.dimensions}
          agregat={etat.dimensions as Record<string, StatDimensionQsort>}
          pyramide={config.echelle.pyramide}
          // Le reclassement est porté par la projection. Repli sur le calcul pur
          // pour les versions figées avant son introduction : même fonction que
          // celle de la finalisation, donc même pyramide.
          niveauxForces={
            etat.niveauxForces ??
            niveauxForcesQsort({
              codes: config.dimensions.map((d) => d.code),
              agregat: etat.dimensions as Record<string, StatDimensionQsort>,
              pyramide: config.echelle.pyramide,
            })
          }
          codesDesaccord={versionCourante.dimensionsEnDesaccord}
          version={{
            version: versionCourante.version,
            nbReferents: versionCourante.nbReferents,
            fige: versionCourante.fige,
          }}
        />
      )}

      {/* Même rôle pour l'échelle fourchette (PAMI) : sans ça, un référentiel
          configuré ET calculé n'était lisible nulle part dans cet onglet. */}
      {config.echelle.kind === "fourchette" && versionCourante && (
        <ReferentielFourchetteCard
          config={config}
          echelle={config.echelle}
          agregat={etat.dimensions as Record<string, StatFourchette>}
        />
      )}

      {/* Les personnes ne sont pas l'objet de cet onglet : leur décompte, leur
          progression et leurs contributions vivent entièrement dans l'onglet
          « Référents ». Un encadré de renvoi ne faisait que dupliquer ce que la
          navigation dit déjà. */}
      <ReferentielItemsCard config={config} />
    </div>
  );
}
